import { useEffect } from "react";
import { numeroALetras, redondearDecimales } from "../../../../utils/utils_number";

import { resumenNceSchema, facturaNceSchema } from "./schemas/zod/nce.schema";

const useNCE_JSON = (factura, setFactura) => {
  useEffect(() => {
    setFactura((prev) => ({
      ...prev,
      identificacion: {
        ...prev.identificacion,
        version: 3,
      },
      resumen: {
        totalNoSuj: 0,
        totalExenta: 0,
        totalGravada: 0,
        subTotalVentas: 0, // Sumatoria de totalNoSuj + totalExenta + totalGravada
        descuNoSuj: 0,
        descuExenta: 0,
        descuGravada: 0,
        totalDescu: 0,
        tributos: [], // ejemplo aplicando IVA: [{ codigo: "20", descripcion: "Impuesto al Valor Agregado 13%", valor: 2.71 }]
        subTotal: 0,
        ivaPerci1: 0,
        ivaRete1: 0,
        reteRenta: 0,
        montoTotalOperacion: 0,
        totalLetras: "",
        condicionOperacion: 1, // 1 contado, 2 credito, 3 otros
      },
    }));
  }, []);

  const itemBase = {
    numItem: 1,
    tipoItem: 1,
    descripcion: "",
    cantidad: "",
    uniMedida: 59, // Código de unidad de medida (Ej: Unidad)
    precioUni: "",
    ventaNoSuj: 0.0,
    ventaExenta: 0.0,
    ventaGravada: 0.0,
    tributos: null, // "20" Se aplica IVA

    // No ocupados de momento:
    codTributo: null,
    numeroDocumento: null,
    montoDescu: 0.0,
    codigo: null,
  };

  const formatearItem = (formData, tipoVenta, comprobanteRelacionado, index) => {
    const cantidad = redondearDecimales(parseFloat(formData.cantidad)) || 0;
    const precioUnitario = redondearDecimales(parseFloat(formData.precioUni)) || 0;
    const montoTotal = redondearDecimales(cantidad * precioUnitario);

    let item = {
      ...formData,
      numItem: index,
      cantidad,
      precioUni: precioUnitario,
      ventaGravada: 0,
      ventaNoSuj: 0,
      ventaExenta: 0,
      tributos: null,
    };

    if (tipoVenta === "1") {
      item.ventaGravada = montoTotal;
      item.tributos = ["20"]; // IVA 13%
    } else if (tipoVenta === "2") {
      item.ventaNoSuj = montoTotal;
    } else if (tipoVenta === "3") {
      item.ventaExenta = montoTotal;
    }

    if (comprobanteRelacionado) {
      item.numeroDocumento = comprobanteRelacionado.codigoGeneracion || null;
    }

    return item;
  };

  const setDocumentoRelacionado = (dte) => {
    setFactura((prev) => ({
      ...prev,
      documentoRelacionado: dte
        ? [
            {
              tipoDocumento: dte?.TipoDTE.codigo,
              tipoGeneracion: 2, // 1 es fisico y 2 es electronico
              numeroDocumento: dte?.codigoGeneracion,
              fechaEmision: dte?.fecEmi,
            },
          ]
        : null,
    }));
  };

  const setEmisor = (emisorBase, _) => {
    const { codEstableMH, codPuntoVentaMH, codEstable, codPuntoVenta, ...emisorLimpio } = emisorBase;
    return emisorLimpio;
  };

  // Función para asignar resumen de totales
  const setResumen = (resumen) => {
    const subTotalVentas = redondearDecimales(resumen.totalNoSuj + resumen.totalExenta + resumen.totalGravada, 2);
    const totalValorTributo = resumen?.tributos?.reduce((acc, tributo) => acc + tributo.valor, 0);
    const montoTotalOperacion = redondearDecimales(subTotalVentas + totalValorTributo - resumen.ivaRete1, 2);

    const resumenValidado = resumenNceSchema.parse({
      ...resumen,
      subTotalVentas,
      subTotal: subTotalVentas,
      montoTotalOperacion,
      totalLetras: numeroALetras(montoTotalOperacion),
    });

    setFactura((prev) => ({
      ...prev,
      resumen: resumenValidado,
    }));
  };

  const buildJson = () => {
    return facturaNceSchema.parse(factura);
  };

  return {
    itemBase,
    formatearItem,
    setEmisor,
    setResumen,
    setDocumentoRelacionado,
    buildJson,
  };
};

export default useNCE_JSON;
